import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Trash2, Mail, Settings as SettingsIcon, ShieldAlert } from "lucide-react";

const CONTACT_EMAIL = "fiestaman38470@gmail.com";

const Step = ({ num, title, children }) => (
  <div className="flex gap-5 py-5 border-b border-border last:border-b-0">
    <div className="w-9 h-9 bg-[#002FA7] text-white flex items-center justify-center flex-shrink-0 font-mono font-bold text-sm">
      {num}
    </div>
    <div className="flex-1">
      <div className="font-semibold text-base mb-1">{title}</div>
      <div className="text-sm text-muted-foreground leading-relaxed">{children}</div>
    </div>
  </div>
);

export default function DeleteAccount() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isFr = i18n.resolvedLanguage === "fr";

  const txt = isFr
    ? {
        marker: "RGPD · DROIT À L'EFFACEMENT",
        h1: "Supprimer votre compte SUX1",
        intro:
          "Vous pouvez demander la suppression définitive de votre compte SUX1 et de toutes les données associées à tout moment. Cette opération est gratuite et irréversible.",
        method_marker: "MÉTHODE 1 · DEPUIS L'APPLICATION",
        method_title: "Suppression en 3 clics depuis votre compte",
        step1: "Connectez-vous",
        step1_body: (
          <>
            Ouvrez SUX1 sur le web (
            <a href="https://sux1.app" className="text-[#002FA7] underline">
              sux1.app
            </a>
            ) ou dans l'application Android, et connectez-vous à votre compte.
          </>
        ),
        step2: "Allez dans Paramètres",
        step2_body:
          "Dans la barre de navigation en haut, cliquez sur « Paramètres » (icône engrenage).",
        step3: "Cliquez sur « Supprimer mon compte »",
        step3_body:
          "Tout en bas de la page Paramètres, vous trouverez le bouton rouge « Supprimer mon compte ». Confirmez la suppression — votre compte sera définitivement effacé immédiatement.",
        email_marker: "MÉTHODE 2 · PAR E-MAIL",
        email_title: "Demande par e-mail (si vous n'avez plus accès à votre compte)",
        email_body: (
          <>
            <p className="mb-3">
              Si vous ne pouvez plus vous connecter à votre compte (mot de passe oublié,
              adresse e-mail compromise, etc.), écrivez-nous directement :
            </p>
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=Demande%20de%20suppression%20de%20compte%20SUX1`}
              data-testid="delete-account-mailto"
              className="inline-flex items-center gap-2 bg-[#002FA7] text-white font-mono uppercase tracking-[0.2em] text-[11px] px-4 py-3 hover:bg-[#001E6B] transition-colors"
            >
              <Mail className="w-3.5 h-3.5" />
              {CONTACT_EMAIL}
            </a>
            <p className="mt-4 text-sm">
              Précisez dans votre e-mail l'adresse associée à votre compte SUX1. Nous
              traiterons votre demande sous 30 jours maximum (généralement sous 72 heures).
            </p>
          </>
        ),
        what_marker: "CE QUI EST SUPPRIMÉ",
        what_title: "Données effacées définitivement",
        what_body: (
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#002FA7] mt-2 flex-shrink-0" />
              <span>
                <strong>Votre compte</strong> : adresse e-mail, mot de passe (haché), nom
                d'affichage, date de création.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#002FA7] mt-2 flex-shrink-0" />
              <span>
                <strong>Vos résumés</strong> : la totalité des résumés générés et associés à
                votre compte.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#002FA7] mt-2 flex-shrink-0" />
              <span>
                <strong>Vos textes originaux</strong> : tous les textes que vous avez soumis
                pour résumé.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#002FA7] mt-2 flex-shrink-0" />
              <span>
                <strong>Vos liens publics</strong> : les URLs <code>/s/&lt;id&gt;</code> deviendront
                inaccessibles immédiatement.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#002FA7] mt-2 flex-shrink-0" />
              <span>
                <strong>Votre statut Pro</strong> : si vous étiez Pro, votre statut sera
                également effacé. ⚠️ Aucun remboursement automatique des crédits achetés.
              </span>
            </li>
          </ul>
        ),
        kept_marker: "CE QUI EST CONSERVÉ",
        kept_title: "Données conservées pour obligations légales",
        kept_body: (
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#FF2A00] mt-2 flex-shrink-0" />
              <span>
                <strong>Factures Stripe</strong> (uniquement si vous étiez Pro) : conservées
                10 ans pour respecter les obligations comptables et fiscales françaises
                (Code de commerce L123-22). Ces factures ne contiennent pas votre numéro de
                carte bancaire, uniquement l'ID Stripe et le montant.
              </span>
            </li>
          </ul>
        ),
        footer_back: "Retour à l'accueil",
        privacy_link: "Voir la politique de confidentialité complète",
      }
    : {
        marker: "GDPR · RIGHT TO ERASURE",
        h1: "Delete your SUX1 account",
        intro:
          "You can request permanent deletion of your SUX1 account and all associated data at any time. This operation is free and irreversible.",
        method_marker: "METHOD 1 · FROM THE APP",
        method_title: "3-click deletion from your account",
        step1: "Sign in",
        step1_body: (
          <>
            Open SUX1 on the web (
            <a href="https://sux1.app" className="text-[#002FA7] underline">
              sux1.app
            </a>
            ) or in the Android app, and sign in to your account.
          </>
        ),
        step2: "Go to Settings",
        step2_body:
          "In the top navigation bar, click on « Settings » (gear icon).",
        step3: "Click on « Delete my account »",
        step3_body:
          "At the bottom of the Settings page, you'll find the red « Delete my account » button. Confirm the deletion — your account will be permanently erased immediately.",
        email_marker: "METHOD 2 · BY EMAIL",
        email_title: "Email request (if you can't access your account)",
        email_body: (
          <>
            <p className="mb-3">
              If you can no longer log into your account (forgotten password, compromised
              email address, etc.), write to us directly:
            </p>
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=SUX1%20account%20deletion%20request`}
              data-testid="delete-account-mailto"
              className="inline-flex items-center gap-2 bg-[#002FA7] text-white font-mono uppercase tracking-[0.2em] text-[11px] px-4 py-3 hover:bg-[#001E6B] transition-colors"
            >
              <Mail className="w-3.5 h-3.5" />
              {CONTACT_EMAIL}
            </a>
            <p className="mt-4 text-sm">
              State the email address associated with your SUX1 account. We will process
              your request within 30 days maximum (typically within 72 hours).
            </p>
          </>
        ),
        what_marker: "WHAT GETS DELETED",
        what_title: "Permanently erased data",
        what_body: (
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#002FA7] mt-2 flex-shrink-0" />
              <span>
                <strong>Your account</strong>: email address, password (hashed), display
                name, creation date.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#002FA7] mt-2 flex-shrink-0" />
              <span>
                <strong>Your summaries</strong>: all generated summaries linked to your
                account.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#002FA7] mt-2 flex-shrink-0" />
              <span>
                <strong>Your original texts</strong>: all texts you have submitted for
                summarization.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#002FA7] mt-2 flex-shrink-0" />
              <span>
                <strong>Your public links</strong>: <code>/s/&lt;id&gt;</code> URLs will become inaccessible
                immediately.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#002FA7] mt-2 flex-shrink-0" />
              <span>
                <strong>Your Pro status</strong>: if you were Pro, your status will also be
                erased. ⚠️ No automatic refund of purchased credits.
              </span>
            </li>
          </ul>
        ),
        kept_marker: "WHAT IS KEPT",
        kept_title: "Data kept for legal obligations",
        kept_body: (
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#FF2A00] mt-2 flex-shrink-0" />
              <span>
                <strong>Stripe invoices</strong> (only if you were Pro): kept for 10 years
                to comply with French accounting and tax obligations (Commercial Code
                L123-22). These invoices do not contain your credit card number, only the
                Stripe ID and the amount.
              </span>
            </li>
          </ul>
        ),
        footer_back: "Back to home",
        privacy_link: "View the full privacy policy",
      };

  return (
    <div className="min-h-screen bg-card">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="h-16 max-w-5xl mx-auto flex items-center px-6 md:px-10 justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="delete-home-link">
            <span className="inline-block w-7 h-7 bg-[#002FA7]" />
            <span
              className="font-black text-2xl tracking-tight"
              style={{ fontFamily: "Outfit" }}
            >
              SUX<span className="text-[#FF2A00]">1</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link
              to={user ? "/" : "/login"}
              className="font-mono uppercase tracking-[0.2em] text-[11px] px-4 py-2 border border-border hover:bg-[#FF2A00] hover:text-white transition-colors"
              data-testid="delete-nav-cta"
            >
              {user ? t("nav.summarize") : t("nav.signin")}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-border bg-[#FF2A00]/5">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 lg:py-20">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 bg-[#FF2A00] text-white flex items-center justify-center">
              <Trash2 className="w-4 h-4" />
            </div>
            <div className="sux-marker text-[#FF2A00]">{txt.marker}</div>
          </div>
          <h1
            className="text-4xl md:text-5xl font-black tracking-tight leading-[1.05] mb-5"
            style={{ fontFamily: "Outfit" }}
          >
            {txt.h1}
          </h1>
          <p className="text-base text-muted-foreground leading-relaxed max-w-2xl">
            {txt.intro}
          </p>
        </div>
      </section>

      {/* Method 1 - in app */}
      <section className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 lg:py-16">
          <div className="sux-marker mb-3">{txt.method_marker}</div>
          <h2
            className="text-2xl md:text-3xl font-black tracking-tight mb-8 leading-tight"
            style={{ fontFamily: "Outfit" }}
          >
            {txt.method_title}
          </h2>
          <div className="border border-border bg-card">
            <Step num="1" title={txt.step1}>{txt.step1_body}</Step>
            <Step num="2" title={txt.step2}>{txt.step2_body}</Step>
            <Step num="3" title={txt.step3}>{txt.step3_body}</Step>
          </div>
        </div>
      </section>

      {/* Method 2 - email */}
      <section className="border-b border-border bg-secondary/30">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 lg:py-16">
          <div className="sux-marker mb-3">{txt.email_marker}</div>
          <h2
            className="text-2xl md:text-3xl font-black tracking-tight mb-6 leading-tight"
            style={{ fontFamily: "Outfit" }}
          >
            {txt.email_title}
          </h2>
          {txt.email_body}
        </div>
      </section>

      {/* What gets deleted */}
      <section className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 lg:py-16">
          <div className="sux-marker mb-3">{txt.what_marker}</div>
          <h2
            className="text-2xl md:text-3xl font-black tracking-tight mb-6 leading-tight"
            style={{ fontFamily: "Outfit" }}
          >
            {txt.what_title}
          </h2>
          {txt.what_body}
        </div>
      </section>

      {/* What is kept */}
      <section className="border-b border-border bg-secondary/30">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 lg:py-16">
          <div className="sux-marker mb-3">{txt.kept_marker}</div>
          <h2
            className="text-2xl md:text-3xl font-black tracking-tight mb-6 leading-tight"
            style={{ fontFamily: "Outfit" }}
          >
            {txt.kept_title}
          </h2>
          {txt.kept_body}
        </div>
      </section>

      <footer className="py-10 px-6 md:px-10 text-center space-y-3">
        <Link
          to="/"
          data-testid="delete-footer-home"
          className="sux-marker hover:text-[#002FA7] transition-colors block"
        >
          ← {txt.footer_back}
        </Link>
        <Link
          to="/privacy"
          data-testid="delete-footer-privacy"
          className="font-mono uppercase tracking-[0.2em] text-[10px] text-muted-foreground hover:text-[#002FA7] transition-colors"
        >
          {txt.privacy_link}
        </Link>
      </footer>
    </div>
  );
}
